import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useSocket } from '../../context/SocketContext'
import { 
  FiUsers, 
  FiMessageSquare, 
  FiCheckSquare, 
  FiActivity,
  FiClock,
  FiTrendingUp,
  FiAlertCircle
} from 'react-icons/fi'
import { RiRobotLine } from 'react-icons/ri'
import AgentCard from '../../components/AgentCard/AgentCard'
import TaskList from '../../components/TaskList/TaskList'
import MessagePreview from '../../components/MessagePreview/MessagePreview'
import './Dashboard.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const Dashboard = () => {
  const { agents, messages, tasks, isConnected } = useSocket()
  const [stats, setStats] = useState({
    totalAgents: 0,
    onlineAgents: 0,
    activeTasks: 0,
    completedTasks: 0,
    unreadMessages: 0,
    responseTime: 0
  })

  // Fetch additional stats
  const { data: systemStats } = useQuery('systemStats', async () => {
    const token = localStorage.getItem('token')
    const response = await fetch(`${API_BASE}/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (!response.ok) throw new Error('Failed to fetch stats')
    return response.json()
  }, {
    refetchInterval: 30000 // Every 30 seconds
  })

  // Update stats when data changes
  useEffect(() => {
    const onlineAgents = agents.filter(a => a.status === 'online').length
    const activeTasks = tasks.filter(t => t.status === 'in_progress').length
    const completedTasks = tasks.filter(t => t.status === 'completed').length
    const unreadMessages = messages.filter(m => !m.is_read).length
    
    setStats({
      totalAgents: agents.length || 17,
      onlineAgents,
      activeTasks,
      completedTasks,
      unreadMessages,
      responseTime: systemStats?.avg_response_time || 0
    })
  }, [agents, tasks, messages, systemStats])

  const statCards = [
    {
      title: 'Online Agents',
      value: `${stats.onlineAgents}/${stats.totalAgents}`,
      icon: <FiUsers />,
      color: 'primary',
      change: '+2 today'
    },
    {
      title: 'Active Tasks',
      value: stats.activeTasks,
      icon: <FiCheckSquare />,
      color: 'warning',
      change: '3 pending'
    },
    {
      title: 'Unread Messages',
      value: stats.unreadMessages,
      icon: <FiMessageSquare />,
      color: 'info',
      change: '2 from Kclaw'
    },
    {
      title: 'Avg Response Time',
      value: `${stats.responseTime}s`,
      icon: <FiClock />,
      color: 'success',
      change: '-15% faster'
    }
  ]

  const recentAgents = agents
    .sort((a, b) => new Date(b.last_heartbeat) - new Date(a.last_heartbeat))
    .slice(0, 6)

  const recentTasks = tasks
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5)

  const recentMessages = messages
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5)

  return (
    <div className="dashboard">
      {/* Welcome header */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Welcome back, Mark</h1>
          <p className="welcome-subtitle">
            Your AI agent network is {isConnected ? 'operational' : 'connecting'}.
            {!isConnected && ' Please check your connection.'}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary">
            <RiRobotLine /> Chat with Kclaw
          </button>
          <button className="btn btn-secondary">
            <FiActivity /> System Report
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className={`stat-card stat-${stat.color}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-title">{stat.title}</div>
            </div>
            <div className="stat-change">{stat.change}</div>
          </div>
        ))}
      </div>

      {/* Main content grid */}
      <div className="dashboard-grid">
        {/* Agent status */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Agent Status</h3>
            <span className="card-badge">{stats.onlineAgents} online</span>
          </div>
          <div className="agent-grid">
            {recentAgents.map(agent => (
              <AgentCard key={agent.agent_id} agent={agent} />
            ))}
          </div>
          <div className="card-footer">
            <button className="btn btn-text">View all agents →</button>
          </div>
        </div>

        {/* Recent tasks */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Tasks</h3>
            <span className="card-badge">{stats.activeTasks} active</span>
          </div>
          <TaskList tasks={recentTasks} compact />
          <div className="card-footer">
            <button className="btn btn-text">View all tasks →</button>
          </div>
        </div>

        {/* Recent messages */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Messages</h3>
            <span className="card-badge">{stats.unreadMessages} unread</span>
          </div>
          <MessagePreview messages={recentMessages} />
          <div className="card-footer">
            <button className="btn btn-text">View all messages →</button>
          </div>
        </div>

        {/* System health */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>System Health</h3>
            <span className="card-badge status-dot connected"></span>
          </div>
          <div className="system-health">
            <div className="health-item">
              <div className="health-label">Backend API</div>
              <div className="health-status success">Operational</div>
            </div>
            <div className="health-item">
              <div className="health-label">Database</div>
              <div className="health-status success">Connected</div>
            </div>
            <div className="health-item">
              <div className="health-label">WebSocket</div>
              <div className="health-status success">Active</div>
            </div>
            <div className="health-item">
              <div className="health-label">Ollama AI</div>
              <div className="health-status warning">Limited</div>
            </div>
          </div>
          <div className="card-footer">
            <div className="health-summary">
              <FiTrendingUp /> System uptime: 99.8%
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="dashboard-card quick-actions">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="actions-grid">
            <button className="action-btn">
              <FiUsers /> Assign Task
            </button>
            <button className="action-btn">
              <FiMessageSquare /> Broadcast Message
            </button>
            <button className="action-btn">
              <FiActivity /> Run Diagnostic
            </button>
            <button className="action-btn">
              <FiAlertCircle /> Report Issue
            </button>
          </div>
        </div>

        {/* Performance metrics */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Performance Trends</h3>
          </div>
          <div className="performance-placeholder">
            <div className="placeholder-chart">
              <div className="chart-bar" style={{ height: '80%' }}></div>
              <div className="chart-bar" style={{ height: '60%' }}></div>
              <div className="chart-bar" style={{ height: '90%' }}></div>
              <div className="chart-bar" style={{ height: '70%' }}></div>
              <div className="chart-bar" style={{ height: '85%' }}></div>
            </div>
            <div className="chart-labels">
              <span>Mon</span>
              <span>Tue</span>
              <span>Wed</span>
              <span>Thu</span>
              <span>Fri</span>
            </div>
          </div>
          <div className="card-footer">
            <div className="trend-info">
              <FiTrendingUp /> Task completion +12% this week
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard